import { format, formatDistance, formatRelative } from "date-fns";

// ============================================================================
// TIMEZONE-SAFE DATE PARSING
// ============================================================================

/**
 * Safely parse a YYYY-MM-DD date string to a Date object in local time.
 * This prevents timezone shifts that occur with new Date("YYYY-MM-DD").
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object in local time
 * @throws Error if date string is invalid
 *
 * @example
 * parseDateString("2026-01-25") // Jan 25 at midnight local time
 */
export function parseDateString(dateString: string): Date {
  if (!validateDateString(dateString)) {
    throw new Error(
      `Invalid date string: ${dateString}. Expected YYYY-MM-DD format.`
    );
  }

  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
}

/**
 * Normalize a timestamp string to an ISO 8601 form that every browser
 * (including iOS Safari) can parse. Handles PostgreSQL-style outputs like
 * "2026-01-25 14:30:00+00" which Safari rejects.
 */
function normalizeTimestampString(timestamp: string): string {
  let normalized = timestamp.trim();
  // "YYYY-MM-DD HH:MM:SS..." → "YYYY-MM-DDTHH:MM:SS..." (Safari requires the T)
  normalized = normalized.replace(/^(\d{4}-\d{2}-\d{2}) /, "$1T");
  // "+0000" / "-0500" → "+00:00" / "-05:00"
  normalized = normalized.replace(/([+-])(\d{2})(\d{2})$/, "$1$2:$3");
  // "+00" / "-05" → "+00:00" / "-05:00"
  normalized = normalized.replace(/([+-])(\d{2})$/, "$1$2:00");
  return normalized;
}

/**
 * Parse an ISO timestamp string to a Date object.
 * Use this for timestamptz columns from the database.
 *
 * Accepts PostgreSQL-style timestamps (with space separator or short TZ offsets)
 * and normalizes them before parsing so iOS Safari does not return Invalid Date.
 *
 * @param timestamp - ISO timestamp string
 * @returns Date object (may be an Invalid Date if the input cannot be parsed)
 *
 * @example
 * parseTimestamp("2026-01-25T14:30:00.000Z")
 */
export function parseTimestamp(timestamp: string): Date {
  const direct = new Date(timestamp);
  if (!isNaN(direct.getTime())) return direct;
  return new Date(normalizeTimestampString(timestamp));
}

function isValidDate(date: Date): boolean {
  return !isNaN(date.getTime());
}

/**
 * Safely parse a date string or timestamp, auto-detecting the format.
 * - YYYY-MM-DD: uses parseDateString (local time)
 * - ISO timestamp: uses parseTimestamp (UTC)
 *
 * @param dateString - Date string in YYYY-MM-DD or ISO format
 * @returns Date object
 */
export function parseDate(dateString: string): Date {
  // Check if it's a YYYY-MM-DD date string
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return parseDateString(dateString);
  }
  // Otherwise treat as ISO timestamp
  return parseTimestamp(dateString);
}

// ============================================================================
// DATE FORMATTING FOR API/DATABASE
// ============================================================================

/**
 * Convert a Date object to YYYY-MM-DD string format for API/database.
 * Uses local time to avoid timezone shifts.
 *
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format
 *
 * @example
 * formatToApiDate(new Date(2026, 0, 25)) // "2026-01-25"
 */
export function formatToApiDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// ============================================================================
// DATE VALIDATION
// ============================================================================

/**
 * Validate a date string is in YYYY-MM-DD format and represents a valid date.
 *
 * @param dateString - Date string to validate
 * @returns true if valid, false otherwise
 *
 * @example
 * validateDateString("2026-01-25") // true
 * validateDateString("2026-13-01") // false (invalid month)
 * validateDateString("01/25/2026") // false (wrong format)
 */
export function validateDateString(dateString: string): boolean {
  // Check format
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return false;
  }

  // Check if date is valid
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

/**
 * Check if a date is in the future (after today).
 *
 * @param date - Date object or YYYY-MM-DD string
 * @returns true if date is after today, false otherwise
 *
 * @example
 * isDateInFuture(new Date(2030, 0, 1)) // true
 * isDateInFuture("2020-01-01") // false
 */
export function isDateInFuture(date: Date | string): boolean {
  const dateObj = typeof date === "string" ? parseDate(date) : date;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dateObj.setHours(0, 0, 0, 0);
  return dateObj > today;
}

// ============================================================================
// DISPLAY FORMATTING (EXISTING FUNCTIONS - UPDATED)
// ============================================================================

/**
 * Format a date to a readable string.
 * Automatically handles YYYY-MM-DD strings timezone-safely.
 *
 * @param date - Date object or date string
 * @param pattern - Date format pattern (default: 'MMM dd, yyyy')
 * @returns Formatted date string
 *
 * @example
 * formatDate(new Date(), "PPP") // "January 25, 2026"
 * formatDate("2026-01-25", "MMM dd, yyyy") // "Jan 25, 2026"
 */
export function formatDate(
  date: Date | string,
  pattern = "MMM dd, yyyy"
): string {
  const dateObj = typeof date === "string" ? parseDate(date) : date;
  if (!isValidDate(dateObj)) return "";
  return format(dateObj, pattern);
}

/**
 * Format a date to a relative time string (e.g., "2 hours ago").
 *
 * @param date - Date object or timestamp string
 * @returns Relative time string
 *
 * @example
 * formatRelativeTime(new Date()) // "less than a minute ago"
 */
export function formatRelativeTime(date: Date | string): string {
  const dateObj = typeof date === "string" ? parseTimestamp(date) : date;
  if (!isValidDate(dateObj)) return "";
  return formatDistance(dateObj, new Date(), { addSuffix: true });
}

/**
 * Format a date relative to now (e.g., "yesterday at 3:20 PM").
 *
 * @param date - Date object or timestamp string
 * @returns Relative date string
 *
 * @example
 * formatRelativeDate(new Date()) // "today at 3:20 PM"
 */
export function formatRelativeDate(date: Date | string): string {
  const dateObj = typeof date === "string" ? parseTimestamp(date) : date;
  if (!isValidDate(dateObj)) return "";
  return formatRelative(dateObj, new Date());
}

/**
 * Format a date to a time string.
 *
 * @param date - Date object or timestamp string
 * @param pattern - Time format pattern (default: 'h:mm a')
 * @returns Formatted time string
 *
 * @example
 * formatTime(new Date()) // "3:20 PM"
 */
export function formatTime(date: Date | string, pattern = "h:mm a"): string {
  const dateObj = typeof date === "string" ? parseTimestamp(date) : date;
  if (!isValidDate(dateObj)) return "";
  return format(dateObj, pattern);
}

/**
 * Format a date to a full date and time string.
 *
 * @param date - Date object or timestamp string
 * @returns Formatted date and time string
 *
 * @example
 * formatDateTime(new Date()) // "Jan 25, 2026 3:20 PM"
 */
export function formatDateTime(date: Date | string): string {
  const dateObj = typeof date === "string" ? parseTimestamp(date) : date;
  if (!isValidDate(dateObj)) return "";
  return format(dateObj, "MMM dd, yyyy h:mm a");
}
